import React from "react";
import parse from "html-react-parser";
import { setTranslationsFunctions } from "./classdojoWebTranslations";

export type TranslationType = string | JSX.Element | JSX.Element[];
export type TranslationContextType = {
  allLanguages: string[];
  translate: (string: string, object?: Record<string, string>) => TranslationType;
  translateWithFallback?: (keyOrObj: string | TranslationParams) => TranslationType;
  translateIfAble?: (string: string, object?: Record<string, string>) => TranslationType;
  translateLocale?: () => string;
  getIsCountryUS: () => boolean;
  translationLocaleAsString?: () => string;
};

type TranslationParams = {
  str: string;
  subs?: Record<string, string>;
  fallback?: string;
};

const TranslationContext = React.createContext<TranslationContextType>({
  translate: (string: string) => string,
  getIsCountryUS: () => false,
  allLanguages: [],
});

type TranslationProviderProps = {
  localeRepo: LocaleRepo;
  children: any;
};

export interface LocaleRepo {
  getValue: (path: string, subs?: Record<string, string>) => any;
  getCurrentLocale: () => string;
  getLanguages: () => string[];
}

const TranslationProvider: React.FC<TranslationProviderProps> = ({ children, localeRepo }) => {
  function translate(path: string, substitutions?: Record<string, string>): TranslationType {
    const result = localeRepo.getValue(path, substitutions);
    if (typeof result === "string") {
      return parse(result); //using parse for now because of this https://github.com/facebook/react/issues/12014
    }
    // @ts-expect-error errors from 437 to 325
    return null;
  }

  function translateWithFallback(keyOrObj: string | TranslationParams): TranslationType {
    if (typeof keyOrObj === "string") {
      return translate(keyOrObj);
    } else {
      const { str, subs, fallback } = keyOrObj;

      let result = localeRepo.getValue(str, subs);
      if (typeof result === "string") {
        if (result.length === 0 && fallback) {
          result = fallback;
        }
        return parse(result); //using parse for now because of this https://github.com/facebook/react/issues/12014
      }
      // @ts-expect-error errors from 437 to 325
      return null;
    }
  }

  function translateIfAble(path: string, substitutions?: Record<string, string>) {
    try {
      const translation = translate(path, substitutions);

      if (typeof translation === "string") return translation;
      else return path;
    } catch (error) {
      return path;
    }
  }

  /**
   * NOTE: Looks like we are not storing a "locales" in pageContext.localeStrings
   * so this is useless and probably should be removed
   */
  function translateLocale() {
    let locale = localeRepo.getCurrentLocale() ?? "en-US";
    if (locale === "en") locale = "en-US";
    return localeRepo.getValue(`locales.${locale}`);
  }

  function translationLocaleAsString() {
    return localeRepo.getCurrentLocale() ?? "en-US";
  }

  const getIsCountryUS = () => translationLocaleAsString() === "en-US";

  const context = {
    allLanguages: localeRepo.getLanguages(),
    translate,
    translateWithFallback,
    translateIfAble,
    translateLocale,
    getIsCountryUS,
    translationLocaleAsString,
  };

  setTranslationsFunctions(context);

  return <TranslationContext.Provider value={context}>{children}</TranslationContext.Provider>;
};

export { TranslationProvider, TranslationContext };
